import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useToastNotification } from '@/hooks/useToastNotification';

export function OrgSwitcher() {
  const { t } = useTranslation('common');
  const { orgMemberships, currentOrgId, switchOrg, isSuperAdmin } = useAuth();
  const { error: toastError } = useToastNotification();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  // Defense-in-depth: filter to active memberships only (joined_at IS NOT NULL)
  // Auth context already filters, but this provides an extra safety layer
  const activeMemberships = orgMemberships.filter(m => m.joined_at !== null);

  // Find current org name from active memberships
  const currentOrg = activeMemberships.find(m => m.org_id === currentOrgId);
  const currentOrgName = currentOrg?.org?.name || t('selectOrganization', 'Select organization');

  // Don't show if user only has one active org and is not super admin
  if (activeMemberships.length <= 1 && !isSuperAdmin) {
    return null;
  }

  const handleSelect = async (orgId: string) => {
    if (orgId === currentOrgId) {
      setOpen(false);
      return;
    }

    setSwitching(true);
    const { error } = await switchOrg(orgId);
    setSwitching(false);

    if (error) {
      toastError(error.message);
    } else {
      setOpen(false);
      // Page will refresh with new context
      window.location.reload();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
          disabled={switching}
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{currentOrgName}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandList>
            <CommandEmpty>{t('noOrganizationsFound', 'No organizations found')}</CommandEmpty>
            <CommandGroup>
              {activeMemberships.map((membership) => (
                <CommandItem
                  key={membership.org_id}
                  value={membership.org_id}
                  onSelect={() => handleSelect(membership.org_id)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentOrgId === membership.org_id ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex flex-col">
                    <span>{membership.org?.name || membership.org_id}</span>
                    <span className="text-xs text-muted-foreground">
                      {membership.role === 'org_admin' ? t('admin', 'Admin') : t('member', 'Member')}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
